import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Button, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import CandidateProfileCard from './CandidateProfileCard';
import OnboardingTaskList from './OnboardingTaskList';
import {
  getCandidateById,
  findCandidateByScanning,
  getOnboardingDetailsById,
  startOnboarding,
  getHasOnboardingStarted
} from 'src/services/onboardingService';
import { getRolePermissions } from 'src/services/authService';
import { validateMultiRolesAndPermissions } from 'src/utils/validate';
import SnackBar from 'src/components/SnackBar/SnackBar';
import CandidateForm from '../candidate/CandidateForm';

const ToDoListPage: React.FC = () => {
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { candidateId } = useParams() as { candidateId: string };
  const [candidate, setCandidate] = useState<any>(null);
  const [permissions, setPermissions] = useState([]);
  const [hasOnboardingStarted, setHasOnboardingStarted] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(undefined);
  const [shouldOpenForm, setShouldOpenForm] = useState(false);
  const [formMode, setFormMode] = useState<
    'view' | 'edit' | 'create' | 'createEmsUser'
  >('createEmsUser');
  const [isEmailAccountCreated, setIsEmailAccountCreated] = useState(false);
  const [isEmsUserCrated, setIsEmsUserCrated] = useState(false);

  const getPermissions = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    getRolePermissions(user.Email)
      .then((res) => {
        const tabs = validateMultiRolesAndPermissions(res);
        const arr = tabs.filter((fil) => fil.name === 'Employee Onboarding');

        if (arr.length > 0) {
          setPermissions(arr.length > 0 ? arr[0].permissions : []);
        } else {
          setPermissions([]);
        }
      })
      .catch((error) => {
        setSnackBarMessage('Failed to get permissions.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  const fetchCandidate = async () => {
    dispatch(AppActions.showLoader());
    try {
      const idNum = Number(candidateId);
      if (Number.isNaN(idNum)) return;

      let found: any = null;

      try {
        const byId = await getCandidateById(idNum);
        const body = typeof byId === 'string' ? JSON.parse(byId) : byId;
        found = body?.data?.data ?? null;
      } catch {
        /* fall back to scanning */
      }

      if (!found) {
        found = await findCandidateByScanning(idNum, 10, 100);
      }

      setCandidate(found);
      setIsEmsUserCrated(
        found?.isEmsUserCrated ?? found?.isEmsUserCreated ?? false
      );
    } catch (e) {
      setSnackBarMessage('Error fetching candidate.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    } finally {
      dispatch(AppActions.hideLoader());
    }
  };

  const fetchOnboardingDetails = async () => {
    dispatch(AppActions.showLoader());

    try {
      const onboardingStatusResponse = await getHasOnboardingStarted(
        parseInt(candidateId)
      );
      setHasOnboardingStarted(onboardingStatusResponse.data.data);
    } catch (err) {
      setSnackBarMessage('Failed to get candidate onboarding status.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    } finally {
      dispatch(AppActions.hideLoader());
    }
  };

  const handleSnackbarMessage = (message: string, severity: string) => {
    setSnackBarMessage(message);
    setSnackbarSeverity(severity);
    setSnackBarOpen(true);
  };

  const handleViewCandidate = async (candidateId: number) => {
    try {
      dispatch(AppActions.showLoader());

      const response = await getCandidateById(candidateId);

      if (response?.data?.statusCode === 200) {
        // Set data and trigger form opening
        setSelectedCandidate(response.data.data);
        setFormMode('createEmsUser');
        setFormOpen(true); // Use formOpen instead of shouldOpenForm
      } else {
        handleSnackbarMessage('Failed to load candidate details', 'error');
      }
    } catch (error) {
      handleSnackbarMessage('Failed to load candidate details', 'error');
    } finally {
      dispatch(AppActions.hideLoader());
    }
  };

  const handleFormSuccess = async (message: string) => {
    setFormOpen(false);
    setSelectedCandidate(undefined);
    // Hide the create button immediately, then sync with latest backend value.
    setIsEmsUserCrated(true);
    await fetchCandidate();
    handleSnackbarMessage(message, 'success');
  };

  const handleFormError = (message: string) => {
    handleSnackbarMessage(message, 'error');
  };

  useEffect(() => {
    fetchOnboardingDetails();
    fetchCandidate();
    getPermissions();
  }, [candidateId]);

  const startOnboardingToDoList = async () => {
    dispatch(AppActions.showLoader());

    try {
      const res = await startOnboarding(CandidateId);

      if (res?.data?.statusCode === 200) {
        setHasOnboardingStarted(true);
        setSnackBarMessage('Onboarding initiated; emails sent to IT and Finance.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
      }
    } catch (err) {
      setSnackBarMessage('Onboarding initiated; email trigger to IT and Finance unsuccessful.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    } finally {
      dispatch(AppActions.hideLoader());
    }
  };

  if (!candidate) return <Typography sx={{ m: 4 }}></Typography>;

  const CandidateId = Number(candidate?.id);

  const expectedJoinDateResolved = candidate?.joinedDate ?? 'Not Provided';

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f7f9fc',
        px: { xs: 2, md: 4 },
        py: 2,
        minWidth: 0
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          style={{
            fontWeight: 700,
            fontSize: '16px',
            fontFamily:
              'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
          }}
        >
          <span
            style={{ cursor: 'pointer', marginRight: '3px' }}
            onClick={() => navigate('/employee-onboarding')}
          >
            Employee Onboarding /
          </span>
          <span style={{ fontWeight: 550 }}>{candidate?.name}</span>
        </Typography>

        <Box display="flex" gap={1} alignItems="center">
          {permissions.some(
            (perm) => perm.name === 'View To Do List' && perm.value === true
          ) &&
            !hasOnboardingStarted && (
              <Button
                sx={{ fontSize: '11px', fontWeight: 'normal' }}
                variant="contained"
                style={{ height: '1.75rem' }}
                onClick={() => startOnboardingToDoList()}
              >
                Start Onboarding
              </Button>
            )}

          {permissions.some(
            (perm) => perm.name === 'Create EMS User' && perm.value === true
          ) &&
            isEmailAccountCreated &&
            !isEmsUserCrated && (
              <Button
                sx={{ fontSize: '11px', fontWeight: 'normal' }}
                variant="contained"
                style={{ height: '1.75rem' }}
                onClick={() => {
                  handleViewCandidate(CandidateId);
                }}
              >
                Create EMS User
              </Button>
            )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex'
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            minHeight: 0,
            width: '100%',
            bgcolor: '#fff',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: 1
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 320 },
              flexShrink: 0,
              borderRight: { md: '1px solid #e0e0e0' },
              p: 2,
              overflowY: 'auto'
            }}
          >
            <CandidateProfileCard
              candidate={{
                fullName: candidate.name || 'Unnamed Candidate',
                designation: candidate.designationCode || 'N/A',
                status: candidate.status,
                imageUrl: candidate.image || '',
                contactNo: candidate.phoneNumber || 'Not Provided',
                joiningDate: expectedJoinDateResolved || 'Not Provided'
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              px: { xs: 2, md: 3 },
              py: 2,
              overflowY: 'auto'
            }}
          >
            <OnboardingTaskList
              candidate={candidate ?? null}
              expectedJoinDate={expectedJoinDateResolved}
              hasOnboardingStarted={hasOnboardingStarted}
              onOrientationStatusChange={setIsEmailAccountCreated}
            />
          </Box>
        </Box>
      </Box>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />

      <CandidateForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        mode={formMode}
        candidate={selectedCandidate}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />
    </Box>
  );
};

export default ToDoListPage;
